import { TFunction } from 'i18next';
import ConfigStatusRenderer from '@cas/components/tables/cells/config-status/ConfigStatusCellRenderer';
import FileStatusRenderer from '../cells/file-status/FileStatusRenderer';
import DeviceTypeCellRenderer from '@cas/components/tables/cells/device-type/DeviceTypeCellRenderer';
import SoftwareStatusCellRenderer from '@cas/components/tables/cells/software-status/SoftwareStatusCellRenderer';
import {
  AssetBasicModel,
  AssetComponentModel,
  AssetFaultBasicModel,
  AssetFaultModel,
  AssetFileModel,
  AssetSelfTestCategorySummaryModel,
  SystemTypePpu,
} from '@customer/store/apis/assetsApi';
import AssetGroupCellRenderer from '../cells/asset-group/AssetGroupCellRenderer';
import AssetNameMismatchCellRenderer from '@cas/components/tables/cells/asset-name-mismatch/AssetNameMismatchCellRender';
import ReportingStatusCellRenderer from '../cells/reporting-status/ReportingStatusCellRenderer';
import {
  getComponentName,
  getGeofenceInteractionType,
  getSelfTestCategory,
  getVehicleInteractionType,
} from '@customer/shared/utils/translationHelpers';
import { ColDef, ColDefField, ValueGetterParams } from 'ag-grid-community';
import { AssetNameData } from '@customer/hooks/assets/useAssetNameTableData';
import SelfTestStatusRenderer from '@cas/components/tables/cells/self-test-status/SelfTestStatusRenderer';
import DurationRenderer from '../cells/duration/DurationRenderer';
import { PartnerResourceModel, TechLibraryDocumentModel } from '@cas/config/documents';
import DateOnlyRenderer from '../cells/date/DateOnlyRenderer';
import FileLinkCellRenderer, {
  FileLinkValue,
} from '@cas/components/tables/cells/file-link/FileLinkCellRenderer';
import DocumentProductFamiliesCellRenderer from '@cas/components/tables/cells/document-product-families/DocumentProductFamiliesCellRenderer';
import DocumentProductsCellRenderer from '@cas/components/tables/cells/document-products/DocumentProductsCellRenderer';
import { CustomCellRendererProps } from 'ag-grid-react';
import NumberRenderer from '../cells/number/NumberRenderer';
import ActiveDurationRenderer, {
  ActiveDurationModel,
} from '../cells/active-duration/ActiveDurationRenderer';
import {
  GISummaryModel,
  VILevel,
  VIListModel,
  VISummaryModel,
} from '@customer/store/apis/interactionsApi';
import PartnerResourceLinkCellRenderer, {
  PartnerLinkValue,
} from '@cas/components/tables/cells/partner-resource-link/PartnerResourceLinkCellRenderer';
import OpenInLibraryLinkRenderer from '../cells/library-link/OpenInLibraryLinkRenderer';
import DocumentTypeCellRenderer from '@cas/components/tables/cells/document-type/DocumentTypeCellRenderer';
import {
  activeDurationComparator,
  activeDurationValueGetter,
} from '../cells/active-duration/ActiveDurationRenderer';
import { getObjectName, getObjectType } from '@cas/utils/translationHelpers';

export const lockColumn = {
  sortable: false,
  lockPinned: true,
  suppressMovable: true,
  resizable: false,
};

/* Asset File Column Defs */

type AssetFileColDefFunc = (t: TFunction) => ColDef<AssetFileModel>;

export const columnDefAssetFilesStatus: AssetFileColDefFunc = t => ({
  field: 'stat',
  headerName: t('Status'),
  cellRenderer: FileStatusRenderer,
});

export const columnDefAssetFilesApplied: AssetFileColDefFunc = t => ({
  headerName: t('Applied'),
  valueGetter: ({ data }: ValueGetterParams<AssetFileModel>) => (data?.app ? 'Applied' : 'Pending'),
  cellRenderer: ConfigStatusRenderer,
});

export const columnDefAssetFilesAppliedAlt: AssetFileColDefFunc = t => ({
  field: 'app',
  headerName: t('Applied'),
  cellRenderer: ({ value }: CustomCellRendererProps<AssetFileModel, boolean>) => {
    return value ? t('Applied') : t('Pending');
  },
});

/* Asset Names Column Defs */

export const columnDefAssetNamesName = (t: TFunction): ColDef<AssetNameData> => ({
  field: 'name',
  headerName: t('AssetName'),
});

export const columnDefAssetNamesSource = (t: TFunction): ColDef<AssetNameData> => ({
  field: 'source',
  headerName: t('Source'),
});

/* Asset Column Defs*/

type AssetColDefFunc = (t: TFunction) => ColDef<AssetBasicModel>;

export const columnDefAssetName: AssetColDefFunc = t => ({
  field: 'name',
  headerName: t('AssetName'),
});

export const columnDefDeviceType: AssetColDefFunc = t => ({
  field: 'hw',
  headerName: t('DeviceType'),
  cellRenderer: DeviceTypeCellRenderer,
});

export const columnDefSoftwareStatus: AssetColDefFunc = t => ({
  field: 'stsCfg',
  headerName: t('SoftwareStatus'),
  cellRenderer: SoftwareStatusCellRenderer,
});

interface SoftwareStatusValue {
  stsCfg?: boolean;
  isPpu?: boolean;
}

const isValidSoftwareStatusValue = (
  value: SoftwareStatusValue | null | undefined,
): value is Required<SoftwareStatusValue> => {
  return !!value && typeof value.stsCfg === 'boolean' && typeof value.isPpu === 'boolean';
};

// Descending order of priority: Up to date, Out of date, PPU
const getPpuOrder = (isPpuA: boolean, isPpuB: boolean): number => {
  if (isPpuA !== isPpuB) {
    return isPpuA ? -1 : 1;
  }
  return 0;
};

const getStatusOrder = (statusA: boolean, statusB: boolean): number => {
  if (statusA !== statusB) {
    return statusA ? 1 : -1;
  }
  return 0;
};

const softwareStatusExcludePpuComparator: ColDef<unknown, SoftwareStatusValue>['comparator'] = (
  valueA,
  valueB,
): number => {
  // First check that the values both exist and contain the required values.
  if (isValidSoftwareStatusValue(valueA) && isValidSoftwareStatusValue(valueB)) {
    // Prioritize sorting based on PPU. Always move PPUs to the bottom of the table when sorting.
    const ppuOrder = getPpuOrder(valueA.isPpu, valueB.isPpu);

    if (ppuOrder !== 0) return ppuOrder;

    // If no there is no PPU difference, sort based on status.
    return getStatusOrder(valueA.stsCfg, valueB.stsCfg);
  }
  // Handle one of the values being null or undefined.
  else if (valueA !== valueB) {
    return valueA ? -1 : 1;
  }

  // Default to no order change if both values are null, undefined.
  return 0;
};

export const columnDefSoftwareStatusExcludePpu: AssetColDefFunc = t => ({
  headerName: t('SoftwareStatus'),
  valueGetter: ({ data }: ValueGetterParams<AssetBasicModel>): SoftwareStatusValue => ({
    stsCfg: data?.stsCfg,
    isPpu: typeof data?.hw !== 'undefined' ? data.hw === SystemTypePpu : undefined,
  }),
  comparator: softwareStatusExcludePpuComparator,
  cellRenderer: ({ value }: CustomCellRendererProps<AssetBasicModel, SoftwareStatusValue>) => {
    return value && value.isPpu === false ? (
      <SoftwareStatusCellRenderer value={value.stsCfg} />
    ) : null;
  },
});

export const columnDefConfigGroupName: AssetColDefFunc = t => ({
  field: 'configGroupName',
  headerName: t('ConfigurationGroup'),
});

export const columnDefCasSoftware: AssetColDefFunc = t => ({
  field: 'casSw',
  headerName: t('CasSoftware'),
});

export const columnDefTotalFaults: AssetColDefFunc = t => ({
  field: 'totFlt',
  headerName: t('Faults'),
});

export const columnDefVehicleInteractionsAlarm: AssetColDefFunc = t => ({
  field: 'totViAm',
  headerName: t(`vi.level.${VILevel.Alarm}`),
  cellRenderer: NumberRenderer,
});

export const columnDefVehicleInteractionsAware: AssetColDefFunc = t => ({
  field: 'totViAe',
  headerName: t(`vi.level.${VILevel.Aware}`),
  cellRenderer: NumberRenderer,
});

export const columnDefVehicleInteractionsAlert: AssetColDefFunc = t => ({
  field: 'totViAt',
  headerName: t(`vi.level.${VILevel.Alert}`),
  cellRenderer: NumberRenderer,
});

export const columnDefVehicleInteractionsTotal: AssetColDefFunc = t => ({
  field: 'totVi',
  headerName: t('Total'),
  cellRenderer: NumberRenderer,
});

export const columnDefGeofenceInteractionsTotal: AssetColDefFunc = t => ({
  field: 'totGi',
  headerName: t('Total'),
  cellRenderer: NumberRenderer,
});

export const columnDefDeviceId: AssetColDefFunc = t => ({
  field: 'did',
  headerName: t('DeviceID'),
});

export const columnDefObjectType: AssetColDefFunc = t => ({
  field: 'obj',
  headerName: t('ObjectType'),
  valueGetter: ({ data }: ValueGetterParams<AssetBasicModel>): string =>
    getObjectType(t, data?.obj),
});

export const columnDefAssetDeviceName: AssetColDefFunc = t => ({
  field: 'dn',
  headerName: `${t('AssetName')} - ${t('AssetNameSourceDevice')}`,
});

export const columnDefAssetGroup: AssetColDefFunc = t => ({
  field: 'ags',
  headerName: t('AssetGroup'),
  cellRenderer: AssetGroupCellRenderer,
});

export const columnDefAssetNameMismatch: AssetColDefFunc = t => ({
  field: 'name',
  headerName: `${t('AssetName')} - ${t('AssetNameSourceServer')}`,
  cellRenderer: AssetNameMismatchCellRenderer,
});

export const columnDefReportingStatus: AssetColDefFunc = t => ({
  field: 'stsRep',
  headerName: t('ReportingStatus'),
  cellRenderer: ReportingStatusCellRenderer,
});

/* Asset Components Column Defs */

type ComponentDefFunc = (t: TFunction) => ColDef<AssetComponentModel>;

export const columnDefFirmwareVersion: ComponentDefFunc = t => ({
  field: 'firmVer',
  headerName: t('ComponentSwFw'),
});

export const columnDefBootloaderVersion: ComponentDefFunc = t => ({
  field: 'bootVer',
  headerName: t('Bootloader Version'),
});

export const columnDefSerialNumber: ComponentDefFunc = t => ({
  field: 'serNo',
  headerName: t('Serial Number'),
});

export const columnDefHardwareRevision: ComponentDefFunc = t => ({
  field: 'hwRev',
  headerName: t('Hardware Revision'),
});

export const columnDefComponentId: ComponentDefFunc = t => ({
  field: 'compId',
  headerName: t('Component ID'),
});

export const columnDefComponentType = (
  t: TFunction,
  hwType?: string,
): ColDef<AssetComponentModel> => ({
  field: 'type',
  headerName: t('Component'),
  valueGetter: ({ data }: ValueGetterParams<AssetComponentModel, string>) =>
    data?.type ? getComponentName(t, data.type, hwType) : null,
});

export const columnDefTechLibraryFilters: ComponentDefFunc = t => ({
  field: 'docFlt',
  headerName: t('RelatedDocuments'),
  valueFormatter: value => '', // Not much we can render here
  cellRenderer: OpenInLibraryLinkRenderer,
});

/* Self Test Summary Column Defs */

type SelfTestSummaryDefFunc = (t: TFunction) => ColDef<AssetSelfTestCategorySummaryModel>;

export const columnDefSelfTestSummaryCategory: SelfTestSummaryDefFunc = t => ({
  field: 'cat',
  headerName: t('SelfTestCategory'),
  valueGetter: ({ data }: ValueGetterParams<AssetSelfTestCategorySummaryModel>): string =>
    getSelfTestCategory(t, data?.cat),
});

export const columnDefSelfTestSummaryActiveFaults: SelfTestSummaryDefFunc = t => ({
  field: 'act',
  headerName: t('ActiveFaults'),
});

export const columnDefSelfTestSummaryTotalFaults: SelfTestSummaryDefFunc = t => ({
  field: 'tot',
  headerName: t('TotalFaults'),
});

/* Asset Fault Column Defs */

type AssetFaultBasicDefFunc = (t: TFunction) => ColDef<AssetFaultBasicModel>;

export const columnDefAssetFaultBasicCategory: AssetFaultBasicDefFunc = t => ({
  field: 'cat',
  headerName: t('FaultCategory'),
  valueGetter: ({ data }: ValueGetterParams<AssetFaultBasicModel>): string =>
    getSelfTestCategory(t, data?.cat),
});

export const columnDefAssetFaultBasicName: AssetFaultBasicDefFunc = t => ({
  field: 'flt',
  headerName: t('TestName'),
  valueGetter: ({ data }: ValueGetterParams<AssetFaultBasicModel>): string =>
    getFaultTest(t, data?.flt),
});

export const columnDefAssetFaultBasicLevel: AssetFaultBasicDefFunc = t => ({
  field: 'lvl',
  headerName: t('FaultLevel'),
});

type AssetFaultDefFunc = (t: TFunction) => ColDef<AssetFaultModel>;

export const columnDefAssetFaultCategory: AssetFaultDefFunc = t => ({
  field: 'cat',
  headerName: t('FaultCategory'),
  valueGetter: ({ data }: ValueGetterParams<AssetFaultModel>): string =>
    getSelfTestCategory(t, data?.cat),
});

export const columnDefAssetActiveFault: AssetFaultDefFunc = t => ({
  field: 'act',
  headerName: t('FaultTestStatus'),
  cellRenderer: SelfTestStatusRenderer,
});

export const columnDefAssetFaultLevel: AssetFaultDefFunc = t => ({
  field: 'lvl',
  headerName: t('FaultLevel'),
});

const FAULT_ID_MIN = 0,
  FAULT_ID_MAX = 96;

const getFaultTest = (t: TFunction, faultId?: number): string => {
  if (typeof faultId !== 'undefined' && faultId >= FAULT_ID_MIN && faultId <= FAULT_ID_MAX) {
    const key = `FaultTest_${faultId}`;
    return t(key);
  }

  return t('Unknown');
};

export const columnDefAssetFaultName: AssetFaultDefFunc = t => ({
  field: 'flt',
  headerName: t('TestName'),
  valueGetter: ({ data }: ValueGetterParams<AssetFaultModel>): string => getFaultTest(t, data?.flt),
});

/* Asset Interactions Column Defs */

type VIColDefFunc = (t: TFunction) => ColDef<VISummaryModel>;

export const columnDefAssetInteractionVI: VIColDefFunc = t => ({
  field: 'vit',
  headerName: t('InteractionType'),
  valueGetter: ({ data }: ValueGetterParams<VISummaryModel>): string =>
    getVehicleInteractionType(t, data?.vit),
});

export const columnDefAssetInteractionVICount: VIColDefFunc = t => ({
  field: 'cnt',
  headerName: t('Total'),
  cellRenderer: NumberRenderer,
});

type VIListColDefFunc = (t: TFunction) => ColDef<VIListModel>;

export const columnDefVIRemoteObjectName: VIListColDefFunc = t => ({
  valueGetter: ({ data }: ValueGetterParams<VIListModel>): [string, boolean] => [
    getObjectName(t, data?.roNa, data?.roDN),
    data?.roDN ?? false,
  ],
});

type GIColDefFunc = (t: TFunction) => ColDef<GISummaryModel>;

export const columnDefAssetInteractionGI: GIColDefFunc = t => ({
  field: 'it',
  headerName: t('InteractionType'),
  valueGetter: ({ data }: ValueGetterParams<GISummaryModel>): string =>
    getGeofenceInteractionType(t, data?.it),
});

export const columnDefAssetInteractionGICount: GIColDefFunc = t => ({
  field: 'cnt',
  headerName: t('Total'),
  cellRenderer: NumberRenderer,
});

/* Technical Library Column Defs */

type TechLibraryDefFunc = (t: TFunction) => ColDef<TechLibraryDocumentModel>;
type PartnerResourceDefFunc = (t: TFunction) => ColDef<PartnerResourceModel>;

export const columnDefTechLibraryReleaseDate: TechLibraryDefFunc = t => ({
  field: 'releaseDate',
  headerName: t('Date'),
  cellRenderer: DateOnlyRenderer,
});

export const columnDefTechLibraryTitle: TechLibraryDefFunc = t => ({
  field: 'title',
  headerName: t('Title'),
});

export const columnDefTechLibraryType: TechLibraryDefFunc = t => ({
  field: 'documentType',
  headerName: t('DocumentType'),
  cellRenderer: DocumentTypeCellRenderer,
});

export const columnDefTechLibraryDownload: TechLibraryDefFunc = t => ({
  headerName: t('File'),
  valueGetter: ({ data }: ValueGetterParams<TechLibraryDocumentModel>): FileLinkValue => ({
    downloadUri: data?.downloadUri,
    fileSize: data?.fileSize,
    fileType: 'PDF',
  }),
  cellRenderer: FileLinkCellRenderer,
});

export const columnDefTechLibraryVersion: TechLibraryDefFunc = t => ({
  field: 'releaseVersion',
  headerName: t('Version'),
});

export const columnDefTechLibraryReference: TechLibraryDefFunc = t => ({
  field: 'releaseVersion',
  headerName: t('Reference'),
});

export const columnDefTechLibraryProductFamilies: TechLibraryDefFunc = t => ({
  field: 'productFamilies',
  headerName: t('ProductFamily'),
  cellDataType: false,
  cellRenderer: DocumentProductFamiliesCellRenderer,
});

export const columnDefTechLibraryProducts: TechLibraryDefFunc = t => ({
  field: 'products',
  headerName: t('Product'),
  cellDataType: false,
  cellRenderer: DocumentProductsCellRenderer,
});

export const columnDefTechLibraryFilename: TechLibraryDefFunc = t => ({
  field: 'filename',
  headerName: t('DocumentName'),
});

export const columnDefPartnerResourceDownload: PartnerResourceDefFunc = t => ({
  headerName: t('File'),
  valueGetter: ({ data }: ValueGetterParams<PartnerResourceModel>): PartnerLinkValue => ({
    uri: data?.downloadUri,
    fileSize: data?.fileSize,
    fileType: data?.fileType,
  }),
  cellRenderer: PartnerResourceLinkCellRenderer,
});

/* Generic Column Defs */

export const colDefDuration = <TData extends { dur: TValue }, TValue extends number | null>(
  headerName: string,
): ColDef<TData, TValue> => ({
  field: 'dur' as ColDefField<TData, TValue>,
  headerName,
  cellRenderer: DurationRenderer,
});

export const colDefActiveDuration = <TData extends ActiveDurationModel>(
  headerName: string,
): ColDef<TData> => ({
  headerName,
  valueGetter: activeDurationValueGetter,
  cellRenderer: ActiveDurationRenderer,
  comparator: activeDurationComparator,
});
